import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Briefcase, LineChart, Building2,
  Settings2, Hand, Bot, CalendarClock,
  ClipboardList, BookOpen, Target, ArrowRight, Check,
} from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { useAuth } from "../contexts/AuthContext";
import { PortalNavButton } from "../components/PortalNavButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_URL = "https://calendly.com/ramzybaroody";

const SAMPLE_COURSES = [
  { id: "sample-1", code: "EXCEL-101", title: "Excel", description: "A college-level course covering spreadsheet fundamentals through advanced analytics — formulas and functions, PivotTables, data cleaning, and dashboard reporting.", color: "yellow" },
  { id: "sample-2", code: "PM-201", title: "Project Management", description: "A college-level course in project planning, scheduling, budgeting, risk, and stakeholder management — covering both Waterfall and Agile/Scrum methodologies.", color: "blue" },
  { id: "sample-3", code: "SYS-301", title: "Business Systems & Processes", description: "A college-level course on designing and improving operational systems — trackers, dashboards, and workflows — grounded in real industry case studies.", color: "mint" },
];

function NavBar({ isAuthed }) {
  const scrollTo = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <nav data-testid="landing-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0b1d3a]/85 border-b border-[#d4af37]/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo-link">
          <img src={LOGO_URL} alt="CFCS" className="h-9 w-auto rounded-sm" />
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm text-[#fff8e1]/75">
          <button onClick={scrollTo("features")} className="hover:text-[#d4af37] transition-colors">Why CFCS</button>
          <button onClick={scrollTo("how")} className="hover:text-[#d4af37] transition-colors">How we work</button>
          <button onClick={scrollTo("programs")} className="hover:text-[#d4af37] transition-colors">Programs</button>
          <Link to="/catalog" data-testid="landing-nav-catalog" className="hover:text-[#d4af37] transition-colors">Course Catalog</Link>
          <Link to="/about" data-testid="landing-nav-about" className="hover:text-[#d4af37] transition-colors">About</Link>
          <button onClick={scrollTo("contact")} className="hover:text-[#d4af37] transition-colors">Contact</button>
        </div>
        <div className="flex items-center gap-3">
          {isAuthed ? (
            <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm text-[#d4af37] hover:underline">Open dashboard →</Link>
          ) : (
            <PortalNavButton testId="nav-signin" />
          )}
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="nav-cta-walkthrough" className="hidden sm:inline-flex items-center gap-1.5 bg-[#d4af37] text-[#0b1d3a] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#e2bd44] transition-colors">
            Book walkthrough <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="pt-32 pb-20 px-6 lg:px-10">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-5 font-semibold">Customized Corporate Training</p>
        <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-[#fff8e1] leading-[1.05]">
          Customized Corporate<br /><span className="text-[#d4af37]">Training Solutions.</span>
        </h1>
        <p className="mt-6 text-xl md:text-2xl text-[#fff8e1]/85 max-w-3xl mx-auto leading-snug font-medium">
          Excel. Project Management. Business Systems. AI Integration.<br className="hidden md:block" /> Designed for your organization.
        </p>
        <p className="mt-6 text-base md:text-lg text-[#fff8e1]/70 max-w-2xl mx-auto leading-relaxed">
          We help organizations improve productivity, strengthen capabilities, and achieve measurable results through practical, hands-on training — built around your specific workflows and team needs.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0b1d3a] px-7 py-3.5 rounded-md font-semibold hover:bg-[#e2bd44] transition-all shadow-[0_8px_30px_rgba(212,175,55,0.25)]">
            Book a 15-min walkthrough <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#programs" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border-2 border-[#d4af37]/50 text-[#fff8e1] px-7 py-3.5 rounded-md font-semibold hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors">
            Explore our programs
          </a>
        </div>
      </div>
    </section>
  );
}

const PROGRAMS = [
  { label: "Excel Training", icon: LineChart },
  { label: "Project Management", icon: Briefcase },
  { label: "Business Systems", icon: Building2 },
  { label: "AI Integration", icon: Sparkles },
];

function IndustriesStrip() {
  return (
    <section className="py-16 border-y border-[#d4af37]/15 bg-[#0a1830]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <p className="text-center text-sm uppercase tracking-[0.18em] text-[#fff8e1]/55 mb-9">Our core programs</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {PROGRAMS.map((it) => (
            <div key={it.label} className="flex flex-col items-center gap-3 text-[#fff8e1]/70 hover:text-[#d4af37] transition-colors">
              <it.icon className="w-9 h-9" strokeWidth={1.5} />
              <span className="text-sm font-medium tracking-wide">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { slug: "customized", icon: Settings2, title: "Customized Programs", body: "Every course is built around your actual workflows, software stack, and team goals — never generic off-the-shelf training." },
  { slug: "practical", icon: Hand, title: "Practical & Applicable", body: "Hands-on exercises using your real data and processes. Participants leave able to do the work, not just describe it." },
  { slug: "ai-enhanced", icon: Bot, title: "AI-Enhanced Learning", body: "Modern AI tools woven into every program — from AI in Excel and Power Query to AI-assisted project planning and reporting." },
  { slug: "flexible", icon: CalendarClock, title: "Flexible Delivery", body: "Onsite, virtual, or hybrid. Scheduled around your operations — half-day workshops, multi-week cohorts, or one-on-one coaching." },
];

function Features() {
  return (
    <section id="features" className="py-24 md:py-32 px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-16">
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Why CFCS</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#fff8e1]">Training that fits how your team actually works.</h2>
          <p className="mt-5 text-lg text-[#fff8e1]/70">No off-the-shelf curriculum. Every program is shaped around the tools your team uses, the outcomes you need, and the schedule that fits your operations.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {FEATURES.map((f) => (
            <div key={f.slug} data-testid={`feature-${f.slug}`} className="p-8 rounded-xl bg-[#11264c] border border-[#d4af37]/25 hover:border-[#d4af37]/60 hover:-translate-y-0.5 transition-all">
              <f.icon className="w-7 h-7 text-[#d4af37] mb-5" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold text-[#fff8e1] mb-2">{f.title}</h3>
              <p className="text-[#fff8e1]/70 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", icon: ClipboardList, title: "Scope", body: "We meet with your team to understand the work, the tools, and the gaps. No assumptions — we build from your actual processes." },
  { n: "02", icon: BookOpen, title: "Deliver", body: "Customized sessions onsite, virtual, or hybrid. Hands-on exercises using your real data. AI tools woven in where they earn their seat." },
  { n: "03", icon: Target, title: "Apply", body: "Participants leave able to do the work — not just describe it. Follow-up coaching and resources keep the skills in use." },
];

function HowItWorks() {
  return (
    <section id="how" className="py-24 md:py-32 px-6 lg:px-10 bg-[#0a1830] border-y border-[#d4af37]/15">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">How we work with you</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#fff8e1]">Scope. Deliver. Apply.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-6">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`step-${s.n}`} className="relative">
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-display text-6xl font-bold text-[#d4af37]/40">{s.n}</span>
                <s.icon className="w-7 h-7 text-[#d4af37]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#fff8e1] mb-3">{s.title}</h3>
              <p className="text-[#fff8e1]/70 leading-relaxed">{s.body}</p>
              {i < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-12 -right-3 w-6 h-px bg-[#d4af37]/30" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CourseCard({ course, onRequest }) {
  return (
    <div data-testid={`public-course-${course.code}`} className="rounded-xl overflow-hidden border-2 border-[#d4af37]/30 hover:border-[#d4af37]/70 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(212,175,55,0.15)] bg-[#11264c]">
      <div className="bg-[#fff8e1] text-[#0b1d3a] px-6 py-5 border-b-2 border-[#d4af37]">
        <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#7d6b32]">{course.code}</p>
        <h3 className="font-display text-xl font-bold mt-1 leading-tight">{course.title}</h3>
      </div>
      <div className="p-6 flex flex-col gap-4 h-[200px]">
        <p className="text-sm text-[#fff8e1]/75 leading-relaxed flex-1">{course.description}</p>
        <button onClick={() => onRequest(course)} data-testid={`request-${course.code}`} className="inline-flex items-center justify-center gap-2 border-2 border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0b1d3a] px-4 py-2.5 rounded-md text-sm font-semibold transition-colors">
          Request enrollment <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function Courses({ courses, onRequest }) {
  // Hybrid catalog: real public courses first, fill remaining slots with samples (always show 3 cards)
  const realCourses = courses.filter((c) => !/^TEST[_-]/i.test(c.code || ""));
  const filled = [...realCourses, ...SAMPLE_COURSES].slice(0, 3);
  const showSamplesNote = realCourses.length < 3;
  return (
    <section id="programs" className="py-24 md:py-32 px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Courses</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#fff8e1]">What you can study.</h2>
          {showSamplesNote && (
            <p className="mt-3 text-sm text-[#fff8e1]/55 italic max-w-xl mx-auto">College-level courses taught by a Ph.D. College Professor. Request enrollment to get started.</p>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {filled.map((c) => <CourseCard key={c.id} course={c} onRequest={onRequest} />)}
        </div>
      </div>
    </section>
  );
}

const INDUSTRIES_OPTIONS = ["Professional Services", "Financial Services", "Healthcare", "Manufacturing", "Technology", "Real Estate", "Non-profit / Government", "Other"];

function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", industry: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "landing" });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="rounded-2xl border-2 border-[#d4af37] bg-[#11264c] p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#d4af37]/15 flex items-center justify-center mb-5">
          <Check className="w-7 h-7 text-[#d4af37]" />
        </div>
        <h3 className="font-display text-2xl font-bold text-[#fff8e1] mb-2">Thanks — we&apos;ll be in touch.</h3>
        <p className="text-[#fff8e1]/70">Someone from the CFCS team will review your inquiry personally. Expect a response within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="rounded-2xl border-2 border-[#d4af37]/30 bg-[#11264c] p-8 md:p-10 space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full name" required>
          <input data-testid="lead-name" required value={form.name} onChange={onChange("name")} className="lead-input" placeholder="Jane Smith" />
        </Field>
        <Field label="Work email" required>
          <input data-testid="lead-email" required type="email" value={form.email} onChange={onChange("email")} className="lead-input" placeholder="jane@company.com" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Company">
          <input data-testid="lead-company" value={form.company} onChange={onChange("company")} className="lead-input" placeholder="Acme Corporation" />
        </Field>
        <Field label="Industry">
          <select data-testid="lead-industry" value={form.industry} onChange={onChange("industry")} className="lead-input">
            <option value="">Select…</option>
            {INDUSTRIES_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
      </div>
      <Field label="What do you need?">
        <textarea data-testid="lead-message" rows={4} value={form.message} onChange={onChange("message")} className="lead-input resize-y" placeholder="We have a team of ~20 who need stronger Excel skills for monthly reporting…" />
      </Field>
      {error && <p data-testid="lead-error" className="text-sm text-red-400">{error}</p>}
      <button data-testid="lead-submit" disabled={submitting} type="submit" className="w-full bg-[#d4af37] text-[#0b1d3a] font-semibold py-3.5 rounded-md hover:bg-[#e2bd44] transition-colors disabled:opacity-50">
        {submitting ? "Sending…" : "Request a walkthrough →"}
      </button>
      <p className="text-xs text-center text-[#fff8e1]/50">No spam. Your details go directly to {BRAND.email || "info@cfcorporatesolutions.com"}.</p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#d4af37] mb-1.5">{label}{required && <span className="text-[#d4af37]/60"> *</span>}</span>
      {children}
    </label>
  );
}

function ContactSection() {
  return (
    <section id="contact" className="py-24 md:py-32 px-6 lg:px-10">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#d4af37] mb-4 font-semibold">Start the conversation</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#fff8e1] mb-6">Start the conversation.</h2>
          <p className="text-lg text-[#fff8e1]/75 leading-relaxed mb-8">
            Tell us about your team, the work they do, and the outcomes you&apos;re after. We&apos;ll come back with a tailored proposal — usually within one business day.
          </p>
          <div className="space-y-3 text-[#fff8e1]/75">
            <p className="flex items-start gap-3"><span className="text-[#d4af37] font-semibold min-w-[80px]">Email</span> <a href="mailto:info@cfcorporatesolutions.com" className="hover:text-[#d4af37]">info@cfcorporatesolutions.com</a></p>
            <p className="flex items-start gap-3"><span className="text-[#d4af37] font-semibold min-w-[80px]">Calendly</span> <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="hover:text-[#d4af37]">Book a 15-min walkthrough →</a></p>
          </div>
        </div>
        <LeadForm />
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#d4af37]/20 py-12 px-6 lg:px-10 bg-[#0a1830]">
      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 items-start">
        <div>
          <img src={LOGO_URL} alt="CFCS" className="h-10 w-auto rounded-sm mb-4" />
          <p className="text-sm text-[#fff8e1]/55 leading-relaxed max-w-xs">Central Florida Corporate Solutions — Built Around Your Business. Delivered for Results.</p>
        </div>
        <div className="text-sm text-[#fff8e1]/65 space-y-2">
          <p className="text-[#d4af37] uppercase tracking-[0.15em] text-xs font-semibold mb-3">Contact</p>
          <p>info@cfcorporatesolutions.com</p>
        </div>
        <div className="text-sm text-[#fff8e1]/65 space-y-2">
          <p className="text-[#d4af37] uppercase tracking-[0.15em] text-xs font-semibold mb-3">Already a user?</p>
          <Link to="/login" data-testid="footer-signin" className="inline-block max-w-[240px] rounded-lg overflow-hidden border border-[#d4af37]/30 hover:border-[#d4af37] hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(212,175,55,0.2)] transition-all">
            <img src="/student-portal-button.png" alt="Student Portal — Your learning workspace. Sign in to the LMS." className="w-full h-auto block" />
          </Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-[#d4af37]/10 text-xs text-[#fff8e1]/40 text-center">
        © {new Date().getFullYear()} Central Florida Corporate Solutions. All rights reserved.
      </div>
    </footer>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/courses`).then((r) => setCourses(r.data || [])).catch(() => setCourses([]));
  }, []);

  const requestCourse = (course) => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      const messageEl = document.querySelector('[data-testid="lead-message"]');
      if (messageEl) {
        messageEl.focus();
        if (!messageEl.value) {
          messageEl.value = `I'd like to enroll my team in ${course.code} — ${course.title}.`;
          messageEl.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    }, 600);
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#0b1d3a] text-[#fff8e1]">
      <NavBar isAuthed={!!user} />
      <Hero />
      <IndustriesStrip />
      <Features />
      <HowItWorks />
      <Courses courses={courses} onRequest={requestCourse} />
      <ContactSection />
      <Footer />
    </div>
  );
}
